import { useNavigate } from "react-router-dom";
import { imgSrc } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Camera, EyeOff, FileText, Loader2, ShieldCheck, Star, Trash2, Upload } from "lucide-react";

export const OnboardingStatusBanner = ({ model, id }) => {
  const navigate = useNavigate();
  if (!model) return null;
  return !model.onboarding?.complete ? (
    <Card data-testid="model-form-onboarding-banner" className="mt-6 rounded-2xl border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)]">
      <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div className="flex items-start gap-3 text-sm">
          <EyeOff className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(43,60%,30%)]" />
          <div>
            <p className="font-semibold text-[hsl(43,60%,22%)]">Hidden from the marketplace — onboarding incomplete</p>
            <p className="text-[hsl(43,50%,28%)]">
              Clients cannot see or book this profile until the government ID scan, the required
              standardized photos, and the AI metrics confirmation are all complete.
            </p>
          </div>
        </div>
        <Button data-testid="model-form-onboarding-button" className="rounded-xl" onClick={() => navigate(`/models/${id}/onboarding`)}>
          Continue onboarding
        </Button>
      </CardContent>
    </Card>
  ) : (
    <div data-testid="model-form-onboarding-complete" className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-[hsl(174,38%,70%)] bg-[hsl(174,38%,96%)] p-4 text-sm">
      <p className="flex items-center gap-2 font-semibold text-[hsl(174,45%,18%)]">
        <ShieldCheck className="h-4 w-4 text-[hsl(174,38%,34%)]" /> Onboarding complete — live in the marketplace
      </p>
      <Button variant="outline" size="sm" className="rounded-xl border bg-card text-xs hover:bg-muted"
        onClick={() => navigate(`/models/${id}/onboarding`)}>
        Manage dataset & metrics
      </Button>
    </div>
  );
};

export const PortfolioPhotosCard = ({ model, uploading, fileRef, onUpload, onRemove, onSetPrimary }) => (
  <Card className="mt-8 rounded-2xl">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base"><Camera className="h-4 w-4" /> Portfolio photos</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
        {(model?.images || []).map((img) => (
          <div key={img.id} data-testid="model-form-image-tile" className="group relative overflow-hidden rounded-xl border">
            <img src={imgSrc(img)} alt="" className="aspect-[3/4] w-full object-cover" />
            {img.is_primary && (
              <span className="absolute left-1.5 top-1.5 rounded-full bg-[hsl(174,38%,34%)] px-2 py-0.5 text-[9px] font-semibold uppercase text-white">Primary</span>
            )}
            <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1.5 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-[opacity] group-hover:opacity-100">
              {!img.is_primary && (
                <button type="button" data-testid="model-form-set-primary-button" title="Set primary" onClick={() => onSetPrimary(img.id)}
                  className="rounded-full bg-white/90 p-1.5 text-foreground hover:bg-white">
                  <Star className="h-3.5 w-3.5" />
                </button>
              )}
              <button type="button" data-testid="model-form-delete-image-button" title="Remove" onClick={() => onRemove(img.id)}
                className="rounded-full bg-white/90 p-1.5 text-destructive hover:bg-white">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          data-testid="model-form-upload-button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed text-muted-foreground transition-[border-color,color] hover:border-[hsl(174,38%,34%)] hover:text-foreground"
        >
          {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
          <span className="text-xs font-medium">{uploading ? "Uploading…" : "Add photos"}</span>
        </button>
      </div>
      <input ref={fileRef} data-testid="model-form-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden
        onChange={(e) => onUpload(e.target.files)} />
      <p className="text-[11px] text-muted-foreground">JPEG, PNG or WebP, up to 8MB each. Stored in secure cloud object storage. Upload a variety: headshots, full body, editorial, movement.</p>
    </CardContent>
  </Card>
);

export const RevocationCard = ({ model, onRevoke }) => (
  <Card data-testid="model-form-revoke-card" className="mt-8 rounded-2xl border-2 border-[hsl(6,62%,80%)]">
    <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
      <div className="max-w-lg">
        <p className="flex items-center gap-2 text-sm font-semibold text-[hsl(6,55%,30%)]">
          <FileText className="h-4 w-4" /> Right of Revocation
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Remove this likeness from LOOKBOOK permanently. The profile disappears from the marketplace, all open
          booking requests are declined, and no new imagery may be generated. Active licensed campaigns may
          finish their agreed run only. All fees already paid are kept.
        </p>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button data-testid="model-form-revoke-button" variant="outline" className="rounded-xl border-[hsl(6,62%,60%)] bg-card text-destructive hover:bg-[hsl(6,62%,96%)]">
            Revoke likeness
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this likeness from LOOKBOOK?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. "{model?.name}" will be removed from the marketplace immediately, every open
              negotiation is declined, and no new bookings can be created. Existing paid licences remain valid
              until their agreed end date, then all usage rights end.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Keep my profile</AlertDialogCancel>
            <AlertDialogAction data-testid="model-form-revoke-confirm" onClick={onRevoke}
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Revoke permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CardContent>
  </Card>
);
